import type { AuthMeResponse } from '@blms/shared/schemas/shiftbook';
import { ApiError } from '@/lib/api-error';

const OWNER_KEY = 'blms-verified-workspace-owner';
let generation = 0;
let epoch = 0;
let expired = false;
let verified = false;
let requestsBlocked = false;
let owner: string | null = null;
const listeners = new Set<() => void>();

export function sessionOwner(session: AuthMeResponse): string | null {
  return session.authenticated ? JSON.stringify([session.user.id, session.user.activeSite]) : null;
}
// Draft namespaces include a site; destructive cleanup and writes follow the user.
export function ownerUser(owner: unknown): string | null {
  if (typeof owner !== 'string') return null;
  try {
    const value: unknown = JSON.parse(owner);
    return Array.isArray(value) && typeof value[0] === 'string' ? value[0] : null;
  } catch {
    return owner; // Opaque owners used by older local fixtures remain isolated.
  }
}
export function sameSessionUser(left: unknown, right: unknown): boolean {
  const user = ownerUser(left);
  return user !== null && user === ownerUser(right);
}
export function ownerSite(owner: string | null): 'aar' | 'fra' | null {
  try {
    const value: unknown = JSON.parse(owner ?? 'null');
    return Array.isArray(value) && (value[1] === 'aar' || value[1] === 'fra') ? value[1] : null;
  } catch {
    return null;
  }
}
export function getSessionEpoch() {
  return epoch;
}
export function getPreviousSessionOwner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}
export function getSessionOwner() {
  return owner;
}
export function getSessionGeneration() {
  return generation;
}
export function isSessionExpired() {
  return expired;
}
export function isSessionVerified() {
  return verified && !expired;
}
export function subscribeSessionBoundary(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
function notify() {
  for (const listener of listeners) listener();
}
export function expireSession() {
  if (expired) return;
  expired = true;
  verified = false;
  epoch += 1;
  notify();
}
export function blockSessionRequests() {
  requestsBlocked = true;
  verified = false;
  generation += 1;
  epoch += 1;
}
export function acceptSessionBoundary(nextOwner: string | null) {
  if (!sameSessionUser(owner, nextOwner)) generation += 1;
  if (owner !== nextOwner || !verified || expired) epoch += 1;
  requestsBlocked = false;
  owner = nextOwner;
  verified = nextOwner !== null;
  expired = false;
  try {
    if (nextOwner) localStorage.setItem(OWNER_KEY, nextOwner);
    else localStorage.removeItem(OWNER_KEY);
  } catch {
    /* Storage may be unavailable; memory boundary still applies. */
  }
  notify();
}
export class ObsoleteSessionError extends Error {
  constructor() {
    super('The authenticated context changed.');
    this.name = 'ObsoleteSessionError';
  }
}
export function isAuthRequiredError(error: unknown) {
  return error instanceof ApiError && error.status === 401;
}
export function shouldRetryRequest(failureCount: number, error: unknown) {
  return (
    !isAuthRequiredError(error) && !(error instanceof ObsoleteSessionError) && failureCount < 2
  );
}

// Only protected API clients use this wrapper. Credential/login requests stay separate.
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (expired) return new Response(null, { status: 401 });
  if (requestsBlocked) throw new ObsoleteSessionError();
  const startedGeneration = generation;
  const startedEpoch = epoch;
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const isRead = method === 'GET' || method === 'HEAD';
  const response = await fetch(input, init);
  if (startedGeneration !== generation) throw new ObsoleteSessionError();
  if (response.status === 401) {
    if (startedEpoch !== epoch) throw new ObsoleteSessionError();
    expireSession();
  }
  if (response.ok) {
    const assertCurrent = () => {
      if (startedGeneration !== generation || (isRead && startedEpoch !== epoch))
        throw new ObsoleteSessionError();
    };
    const json = response.json?.bind(response);
    const text = response.text?.bind(response);
    const blob = response.blob?.bind(response);
    if (json)
      response.json = async () => {
        const value = await json();
        assertCurrent();
        return value;
      };
    if (text)
      response.text = async () => {
        const value = await text();
        assertCurrent();
        return value;
      };
    if (blob)
      response.blob = async () => {
        const value = await blob();
        assertCurrent();
        return value;
      };
  }
  return response;
}
